import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
    Typography,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord'
import FlagOutlinedIcon from '@material-ui/icons/FlagOutlined'
import FlagIcon from '@material-ui/icons/Flag'
import { Container } from '../../Container'
import { AdminChatView } from './AdminChatView'
import { ProblemFlagPopover } from './ProblemFlagPopover'
import { ProblemCategory } from './analysisTypes'
import { useAdminLiveFeedContext } from '../../../contexts/AdminLiveFeedContext'
import { useConversationProblemsByIds } from '../../../hooks/useConversationProblemsByIds'

function formatDuration(connectedAt: string): string {
    const connectedTimestamp = Number(connectedAt) * 1000
    const now = Date.now()
    const diffMs = now - connectedTimestamp
    if (diffMs < 0) return 'just now'

    const totalSeconds = Math.floor(diffMs / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    if (hours > 0) return `${hours}h ${minutes}m`
    if (minutes > 0) return `${minutes}m ${seconds}s`
    return `${seconds}s`
}

function formatRelativeTime(iso: string | null): string {
    if (!iso) return 'No messages yet'
    const diffMs = Date.now() - new Date(iso).getTime()
    if (diffMs < 0) return 'just now'
    const seconds = Math.floor(diffMs / 1000)
    if (seconds < 60) return `${seconds}s ago`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    return `${Math.floor(hours / 24)}d ago`
}

interface Props {
    onCreateProblem: (data: {
        conversation: number
        category: ProblemCategory
        notes?: string
    }) => void
    onUpdateProblem: (data: {
        id: number
        category: ProblemCategory
        notes?: string
    }) => void
    onDeleteProblem: (id: number) => void
    problemSaving?: boolean
}

export const LiveTab: React.FC<Props> = ({
    onCreateProblem,
    onUpdateProblem,
    onDeleteProblem,
    problemSaving,
}) => {
    const theme = useTheme()
    const { conversations, hasInitialState } = useAdminLiveFeedContext()
    const [selectedConversationId, setSelectedConversationId] = useState<
        number | null
    >(null)
    const [flagAnchor, setFlagAnchor] = useState<HTMLElement | null>(null)
    const [flagConversationId, setFlagConversationId] = useState<number | null>(
        null,
    )
    const [, setTick] = useState(0)
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

    const sortedConversations = useMemo(
        () =>
            [...conversations].sort((a, b) => {
                const aTs = a.last_user_message_at
                    ? new Date(a.last_user_message_at).getTime()
                    : Number(a.connected_at) * 1000
                const bTs = b.last_user_message_at
                    ? new Date(b.last_user_message_at).getTime()
                    : Number(b.connected_at) * 1000
                return bTs - aTs
            }),
        [conversations],
    )

    const conversationIds = useMemo(
        () => sortedConversations.map((c) => c.id),
        [sortedConversations],
    )

    const { problemsByConversationId } = useConversationProblemsByIds(
        conversationIds,
        conversations.length > 0,
    )

    // Duration ticker — re-render every 30s to update durations
    useEffect(() => {
        intervalRef.current = setInterval(() => {
            setTick((t) => t + 1)
        }, 30000)
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
    }, [])

    if (selectedConversationId !== null) {
        const conv = conversations.find((c) => c.id === selectedConversationId)
        return (
            <AdminChatView
                conversationId={selectedConversationId}
                conversationLabel={
                    conv
                        ? `${conv.user.name} — ${conv.company.name}`
                        : `Conversation #${selectedConversationId}`
                }
                onBack={() => setSelectedConversationId(null)}
            />
        )
    }

    if (!hasInitialState) {
        return (
            <Container
                style={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }}
            >
                <CircularProgress size={32} />
            </Container>
        )
    }

    if (conversations.length === 0) {
        return (
            <Container
                style={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }}
            >
                <Typography variant="body1" color="textSecondary">
                    No live conversations at the moment.
                </Typography>
            </Container>
        )
    }

    return (
        <Container
            style={{
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
            }}
        >
            <Container
                style={{
                    padding: theme.spacing(1, 0),
                    alignItems: 'center',
                    gap: theme.spacing(1),
                    flexShrink: 0,
                }}
            >
                <FiberManualRecordIcon
                    style={{ color: '#4caf50', fontSize: 12 }}
                />
                <Typography variant="subtitle1">
                    {conversations.length} live
                    {conversations.length !== 1
                        ? ' conversations'
                        : ' conversation'}
                </Typography>
            </Container>

            <TableContainer
                component={Paper}
                variant="outlined"
                style={{ flex: 1, overflow: 'auto' }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Workspace</TableCell>
                            <TableCell>Platform</TableCell>
                            <TableCell>Last Activity</TableCell>
                            <TableCell align="right">User Messages</TableCell>
                            <TableCell>Duration</TableCell>
                            <TableCell align="center">Problem</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedConversations.map((conv) => {
                            const lastActivityLabel = formatRelativeTime(
                                conv.last_user_message_at,
                            )
                            const lastActivityMuted =
                                conv.last_user_message_at === null
                            const isFlagged = problemsByConversationId.has(
                                conv.id,
                            )
                            return (
                                <TableRow
                                    key={conv.id}
                                    hover
                                    style={{ cursor: 'pointer' }}
                                    onClick={() =>
                                        setSelectedConversationId(conv.id)
                                    }
                                >
                                    <TableCell>
                                        <Typography variant="body2">
                                            {conv.user.name}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                        >
                                            {conv.user.email}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{conv.company.name}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={conv.platform}
                                            style={{
                                                backgroundColor:
                                                    conv.platform === 'WEB'
                                                        ? '#e3f2fd'
                                                        : '#f3e5f5',
                                                color:
                                                    conv.platform === 'WEB'
                                                        ? '#1565c0'
                                                        : '#7b1fa2',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            color={
                                                lastActivityMuted
                                                    ? 'textSecondary'
                                                    : 'textPrimary'
                                            }
                                        >
                                            {lastActivityLabel}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {conv.message_count}
                                    </TableCell>
                                    <TableCell>
                                        {formatDuration(conv.connected_at)}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip
                                            title={
                                                isFlagged
                                                    ? 'Edit Problem'
                                                    : 'Flag as Problem'
                                            }
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setFlagConversationId(
                                                        conv.id,
                                                    )
                                                    setFlagAnchor(
                                                        e.currentTarget,
                                                    )
                                                }}
                                            >
                                                {isFlagged ? (
                                                    <FlagIcon
                                                        style={{
                                                            color:
                                                                theme.palette
                                                                    .error.main,
                                                        }}
                                                    />
                                                ) : (
                                                    <FlagOutlinedIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        {conv.takeover_admin_id ? (
                                            <Chip
                                                size="small"
                                                label="Taken Over"
                                                style={{
                                                    backgroundColor: '#fff3e0',
                                                    color: '#e65100',
                                                }}
                                            />
                                        ) : (
                                            <Chip
                                                size="small"
                                                label="Active"
                                                style={{
                                                    backgroundColor: '#e8f5e9',
                                                    color: '#2e7d32',
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {flagConversationId !== null && (
                <ProblemFlagPopover
                    anchorEl={flagAnchor}
                    onClose={() => {
                        setFlagAnchor(null)
                        setFlagConversationId(null)
                    }}
                    conversationId={flagConversationId}
                    existingProblem={
                        problemsByConversationId.get(flagConversationId) ?? null
                    }
                    onCreate={onCreateProblem}
                    onUpdate={onUpdateProblem}
                    onDelete={onDeleteProblem}
                    saving={problemSaving}
                />
            )}
        </Container>
    )
}
